// ToolbarPlugin.tsx
// Refactor goals:
// - Replace custom dropdown positioning + portals with Mantine <Menu>, <Popover>, <Divider>, <ActionIcon>, <Group>, <Select>, <TextInput>
// - Replace custom "divider" element with Mantine <Divider>
// - Replace icon background-image sprites with Tabler icons (or your own icon wrapper)
// - Keep Lexical logic intact
//
// NOTE: I stubbed a few "yours" imports (InlineImageDialog, and optionally a shared icon component).
// Replace stubs with your actual paths/types.

import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode,
  REMOVE_LIST_COMMAND,
} from "@lexical/list";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $createHeadingNode, $createQuoteNode, $isHeadingNode } from "@lexical/rich-text";
import { $isAtNodeEnd, $wrapNodes } from "@lexical/selection";
import { $getNearestNodeOfType, mergeRegister } from "@lexical/utils";
import { ActionIcon, Divider, Group, Menu, Tooltip } from "@mantine/core";
import {
  IconAlignJustified,
  IconBold,
  IconCode,
  IconH1,
  IconH2,
  IconItalic,
  IconLink,
  IconList,
  IconListNumbers,
  IconPhoto,
  IconProps,
  IconQuote,
  IconStrikethrough,
  IconUnderline,
} from "@tabler/icons-react";
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  FORMAT_TEXT_COMMAND,
  LexicalEditor,
  SELECTION_CHANGE_COMMAND,
} from "lexical";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { INSERT_INLINE_IMAGE_COMMAND } from "./InlineImagePlugin";

const LowPriority = 1;

type BlockType = "root" | "paragraph" | "quote" | "h1" | "h2" | "ul" | "ol";

const supportedBlockTypes = new Set<BlockType>(["root", "paragraph", "quote", "h1", "h2", "ul", "ol"]);

const blockTypeMeta: Record<
  BlockType,
  { label: string; icon: React.ForwardRefExoticComponent<IconProps & React.RefAttributes<SVGSVGElement>> }
> = {
  root: { label: "Normal", icon: IconAlignJustified },
  paragraph: { label: "Normal", icon: IconAlignJustified },
  quote: { label: "Sitat", icon: IconQuote },
  h1: { label: "Stor overskrift", icon: IconH1 },
  h2: { label: "Liten overskrift", icon: IconH2 },
  ul: { label: "Punktliste", icon: IconList },
  ol: { label: "Nummerert liste", icon: IconListNumbers },
};

function getSelectedNode(selection: any) {
  const anchor = selection.anchor;
  const focus = selection.focus;
  const anchorNode = anchor.getNode();
  const focusNode = focus.getNode();
  if (anchorNode === focusNode) return anchorNode;

  const isBackward = selection.isBackward();
  if (isBackward) return $isAtNodeEnd(focus) ? anchorNode : focusNode;
  return $isAtNodeEnd(anchor) ? focusNode : anchorNode;
}

function BlockTypeMenu({ editor, blockType }: { editor: LexicalEditor; blockType: BlockType }) {
  const setParagraph = () => {
    if (blockType === "paragraph") return;
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $wrapNodes(selection, () => $createParagraphNode());
      }
    });
  };

  const setHeading = (tag: "h1" | "h2") => {
    if (blockType === tag) return;
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $wrapNodes(selection, () => $createHeadingNode(tag));
      }
    });
  };

  const setQuote = () => {
    if (blockType === "quote") return;
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $wrapNodes(selection, () => $createQuoteNode());
      }
    });
  };

  const toggleBulletList = () => {
    if (blockType !== "ul") editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, null);
    else editor.dispatchCommand(REMOVE_LIST_COMMAND, null);
  };

  const toggleNumberedList = () => {
    if (blockType !== "ol") editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, null);
    else editor.dispatchCommand(REMOVE_LIST_COMMAND, null);
  };

  const current = blockTypeMeta[blockType] ?? blockTypeMeta.paragraph;
  const CurrentIcon = current.icon;

  return (
    <Menu withinPortal shadow="md" position="bottom-start" offset={6}>
      <Menu.Target>
        <ActionIcon variant="subtle" aria-label="Block type">
          <CurrentIcon size={16} />
        </ActionIcon>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Item leftSection={<IconAlignJustified size={16} />} onClick={setParagraph}>
          Normal
        </Menu.Item>
        <Menu.Item leftSection={<IconH1 size={16} />} onClick={() => setHeading("h1")}>
          Stor overskrift
        </Menu.Item>
        <Menu.Item leftSection={<IconH2 size={16} />} onClick={() => setHeading("h2")}>
          Liten overskrift
        </Menu.Item>

        <Menu.Divider />

        <Menu.Item leftSection={<IconList size={16} />} onClick={toggleBulletList}>
          Punktliste
        </Menu.Item>
        <Menu.Item leftSection={<IconListNumbers size={16} />} onClick={toggleNumberedList}>
          Nummerert liste
        </Menu.Item>

        <Menu.Divider />

        <Menu.Item leftSection={<IconQuote size={16} />} onClick={setQuote}>
          Sitat
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

function ToolbarToggle({
  label,
  active,
  icon,
  onClick,
  disabled,
}: {
  label: string;
  active: boolean;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <Tooltip label={label} withArrow>
      <ActionIcon variant={active ? "filled" : "subtle"} onClick={onClick} aria-label={label} disabled={disabled}>
        {icon}
      </ActionIcon>
    </Tooltip>
  );
}

const ToolbarPlugin = () => {
  const [editor] = useLexicalComposerContext();
  const [blockType, setBlockType] = useState<BlockType>("paragraph");

  const [isLink, setIsLink] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isCode, setIsCode] = useState(false);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) return;

    const anchorNode = selection.anchor.getNode();
    const element = anchorNode.getKey() === "root" ? anchorNode : anchorNode.getTopLevelElementOrThrow();
    const elementKey = element.getKey();

    const elementDOM = editor.getElementByKey(elementKey);
    if (elementDOM) {
      if ($isListNode(element)) {
        const parentList = $getNearestNodeOfType(anchorNode, ListNode);
        const type = parentList ? parentList.getTag() : element.getTag();
        setBlockType(type as BlockType);
      } else {
        const type = $isHeadingNode(element) ? element.getTag() : element.getType();
        setBlockType(type as BlockType);
      }
    }

    setIsBold(selection.hasFormat("bold"));
    setIsItalic(selection.hasFormat("italic"));
    setIsUnderline(selection.hasFormat("underline"));
    setIsStrikethrough(selection.hasFormat("strikethrough"));
    setIsCode(selection.hasFormat("code"));

    const node = getSelectedNode(selection);
    const parent = node.getParent();
    setIsLink($isLinkNode(parent) || $isLinkNode(node));
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }: any) => {
        editorState.read(() => updateToolbar());
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateToolbar();
          return false;
        },
        LowPriority,
      ),
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        () => {
          return false;
        },
        LowPriority,
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        () => {
          return false;
        },
        LowPriority,
      ),
    );
  }, [editor, updateToolbar]);

  const toggleLink = useCallback(
    (editor: LexicalEditor, urlFromInput: string) => {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, isLink ? null : urlFromInput);
    },
    [editor, isLink],
  );

  function normalizeUrl(url: string): string {
    const trimmed = url.trim();
    if (!trimmed) return "";
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
  }

  function getActiveLink(editor: LexicalEditor): {
    url: string;
    text: string;
  } | null {
    let result: { url: string; text: string } | null = null;

    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;

      const node = selection.anchor.getNode();
      const parent = node.getParent();

      if ($isLinkNode(parent)) {
        result = {
          url: parent.getURL(),
          text: normalizeUrl(parent.getTextContent()),
        };
      } else {
        result = {
          url: "",
          text: normalizeUrl(node.getTextContent()),
        };
      }
    });

    return result;
  }

  return (
    <Group gap="xs" wrap="wrap" align="center">
      {/* Block type */}
      {supportedBlockTypes.has(blockType) && (
        <>
          <BlockTypeMenu editor={editor} blockType={blockType} />
          <Divider orientation="vertical" />
        </>
      )}

      <>
        {/* Text formatting */}
        <ToolbarToggle
          label="Fet"
          active={isBold}
          icon={<IconBold size={18} />}
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold")}
        />
        <ToolbarToggle
          label="Kursiv"
          active={isItalic}
          icon={<IconItalic size={18} />}
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic")}
        />
        <ToolbarToggle
          label="Understreking"
          active={isUnderline}
          icon={<IconUnderline size={18} />}
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline")}
        />
        <ToolbarToggle
          label="Gjennomstreking"
          active={isStrikethrough}
          icon={<IconStrikethrough size={18} />}
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough")}
        />
        <ToolbarToggle
          label="Kode"
          active={isCode}
          icon={<IconCode size={18} />}
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "code")}
        />

        <Divider orientation="vertical" />

        {/* Link */}
        <ToolbarToggle
          label={isLink ? "Fjern lenke" : "Sett inn lenke"}
          active={isLink}
          icon={<IconLink size={18} />}
          onClick={() => {
            const currentUrl = getActiveLink(editor);

            if (currentUrl == null) {
              return;
            }

            toggleLink(editor, currentUrl.text);
          }}
        />

        {/* Image insert */}
        <InlineImageToolbarButton editor={editor} />
      </>
    </Group>
  );
};

export default ToolbarPlugin;

// InlineImageToolbarButton.tsx

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Failed reading file"));
    reader.onload = () => resolve(String(reader.result));
    reader.readAsDataURL(file);
  });
}

export function InlineImageToolbarButton({ editor }: { editor: LexicalEditor }) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = "";

          if (!file) return;

          const src = await fileToDataUrl(file);
          editor.dispatchCommand(INSERT_INLINE_IMAGE_COMMAND, {
            altText: null,
            src,
            showCaption: null,
            position: null,
          });
        }}
      />

      <Tooltip label="Sett inn bilde" withArrow>
        <ActionIcon variant="subtle" size="lg" aria-label="Sett inn bilde" onClick={() => inputRef.current?.click()}>
          <IconPhoto size={18} />
        </ActionIcon>
      </Tooltip>
    </>
  );
}
